import { computed, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Nullable } from 'primeng/ts-helpers';
import { finalize, Observable, take } from 'rxjs';

export class ControlFilteredOptionLoader<TOption, TParams> {
  private selectedBeforeRequest: TOption[] = [];
  private $initialOptions = signal<TOption[]>([]);
  private $responseOptions = signal<TOption[]>([]);

  protected control: Nullable<FormControl<Nullable<string[]>>> = null;
  private optionKeyField: Nullable<keyof TOption> = null;
  private requester: Nullable<((params: TParams) => Observable<TOption[]>)> = null;

  readonly $options = computed<TOption[]>(() => {
    const requiredOptions = this.getFilteredOptions(this.selectedBeforeRequest, this.$initialOptions());

    return this.getFilteredOptions(
      requiredOptions,
      this.$responseOptions()
    );
  });
  readonly $loading = signal(false);

  applyConfigs(applyingConfigs: {
    control?: FormControl<Nullable<string[]>>,
    optionKeyField?: keyof TOption,
    requester?: (params: TParams) => Observable<TOption[]>,
    initialOptions?: TOption[],
  }): void {
    const configs = {
      control: this.control,
      optionKeyField: this.optionKeyField,
      requester: this.requester,
      initialOptions: this.$initialOptions(),
      ...applyingConfigs,
    };

    this.control = configs.control;
    this.optionKeyField = configs.optionKeyField;
    this.requester = configs.requester;
    this.$initialOptions.set(configs.initialOptions ?? []);
  }

  load(params: TParams): void {
    if (!this.control || !this.optionKeyField || !this.requester) {
      return;
    }

    this.selectedBeforeRequest = (this.control.value ?? [])
      .map((id) => this.$options().find((option) => option[this.optionKeyField as keyof TOption] === id))
      .filter(Boolean) as TOption[];

    this.$loading.set(true);
    this.requester(params)
      .pipe(
        take(1),
        finalize(() => this.$loading.set(false)),
      )
      .subscribe((options: TOption[]) => this.$responseOptions.set(options));
  };

  private getFilteredOptions(
    requiredOptions: TOption[],
    otherOptions: TOption[],
  ): TOption[] {
    const responseOptionsIds = new Set(otherOptions.map(({ [this.optionKeyField!]: key }) => key));
    const additionalOptions = requiredOptions.filter(({ [this.optionKeyField!]: key }) => !responseOptionsIds.has(key));
    return [...additionalOptions, ...otherOptions];
  }


}
